/**
 * Discovery Review Queue — Curation UI v2
 * Route: /review
 *
 * Keyboard-first daily driver for clearing ambient 4-rail volume.
 * j/k navigate, single-key promote/reject/defer, batch-with-visibility.
 * Dispatch: EX-P1 Part C.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Badge, Button, Spinner } from 'flowbite-react';
import {
  HiCheck,
  HiX,
  HiEye,
  HiRefresh,
  HiExclamation,
  HiLightBulb,
  HiClock,
  HiChevronDown,
  HiChevronUp,
} from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { projectsApi } from '../api/projects';
import { discoveriesApi } from '../api/discoveries';
import type { Discovery } from '../api/discoveries';
import type { Project } from '../types';

const REASON_CODES = [
  { value: 'duplicate', label: 'Duplicate', key: '1' },
  { value: 'too_specific', label: 'Too Specific', key: '2' },
  { value: 'contradicts_best_practice', label: 'Contradicts', key: '3' },
  { value: 'quality_issues', label: 'Quality', key: '4' },
] as const;

const DEFER_PRESETS = [
  { label: '1 day', days: 1, key: '1' },
  { label: '3 days', days: 3, key: '2' },
  { label: '1 week', days: 7, key: '3' },
  { label: '2 weeks', days: 14, key: '4' },
] as const;

type InlineMode = null | 'reject' | 'defer';

const DiscoveryReview: React.FC = () => {
  const { user } = useStatus();

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [discoveries, setDiscoveries] = useState<Discovery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActioning, setIsActioning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Keyboard navigation
  const [focusIndex, setFocusIndex] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [inlineMode, setInlineMode] = useState<InlineMode>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Filter
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => { loadProjects(); }, []);
  useEffect(() => { if (selectedProjectId) loadDiscoveries(); }, [selectedProjectId]);

  const loadProjects = async () => {
    try {
      const response = await projectsApi.list();
      if (response.success && response.data) {
        const activeProjects =
          (response.data as any).Records?.filter(
            (p: Project) => p.status !== 'archived'
          ) || response.data.filter((p: Project) => p.status !== 'archived');
        setProjects(activeProjects);
        if (activeProjects.length > 0 && !selectedProjectId) {
          setSelectedProjectId(activeProjects[0].project_id);
        }
      }
    } catch {
      setError('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDiscoveries = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await discoveriesApi.getDiscoveries(selectedProjectId, 'proposed');
      if (response.success && response.data) {
        const data = (response.data as any).data || response.data;
        setDiscoveries(data.records || []);
        setFocusIndex(0);
        setSelected(new Set());
        setInlineMode(null);
      } else {
        setError(response.error || 'Failed to load discoveries');
      }
    } catch {
      setError('Failed to load discovery queue');
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() =>
    discoveries.filter(d => sourceFilter === 'all' || d.source === sourceFilter),
    [discoveries, sourceFilter]
  );

  const sourceStats = useMemo(() => {
    const stats: Record<string, number> = {};
    for (const d of discoveries) {
      stats[d.source] = (stats[d.source] || 0) + 1;
    }
    return stats;
  }, [discoveries]);

  const showToast = useCallback((text: string, type: 'success' | 'error' = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const removeCard = useCallback((id: string) => {
    setDiscoveries(prev => prev.filter(d => d.id !== id));
    setSelected(prev => { const next = new Set(prev); next.delete(id); return next; });
  }, []);

  const doAction = useCallback(async (
    discovery: Discovery,
    action: 'approve' | 'reject' | 'observe',
    extra?: { reason_code?: string; reason?: string }
  ) => {
    setIsActioning(discovery.id);
    try {
      const response = await discoveriesApi.transitionStandard(discovery.id, {
        action,
        ...extra,
      });
      if (response.success) {
        removeCard(discovery.id);
        const verb = action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Deferred';
        showToast(`${verb}: ${discovery.name}`);
      } else {
        showToast(response.error || `Failed to ${action}`, 'error');
      }
    } catch {
      showToast(`Failed to ${action}`, 'error');
    } finally {
      setIsActioning(null);
      setInlineMode(null);
    }
  }, [removeCard, showToast]);

  const doBatchAction = useCallback(async (action: 'approve' | 'observe') => {
    if (selected.size === 0) return;
    const targets = filtered.filter(d => selected.has(d.id));
    setIsActioning('batch');
    let succeeded = 0;
    for (const d of targets) {
      try {
        const response = await discoveriesApi.transitionStandard(d.id, { action });
        if (response.success) {
          removeCard(d.id);
          succeeded++;
        }
      } catch { /* continue */ }
    }
    setIsActioning(null);
    const verb = action === 'approve' ? 'Approved' : 'Deferred';
    showToast(`${verb} ${succeeded}/${targets.length} items`);
  }, [selected, filtered, removeCard, showToast]);

  const scrollToCard = useCallback((index: number) => {
    const el = cardRefs.current.get(index);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  // Keyboard handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (inlineMode === 'reject') {
        const code = REASON_CODES.find(r => r.key === e.key);
        if (code && filtered[focusIndex]) {
          e.preventDefault();
          doAction(filtered[focusIndex], 'reject', { reason_code: code.value });
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setInlineMode(null); return; }
        return;
      }

      if (inlineMode === 'defer') {
        const preset = DEFER_PRESETS.find(p => p.key === e.key);
        if (preset && filtered[focusIndex]) {
          e.preventDefault();
          const deferDate = new Date();
          deferDate.setDate(deferDate.getDate() + preset.days);
          doAction(filtered[focusIndex], 'observe', {
            reason: `deferred until ${deferDate.toISOString().slice(0, 10)}`,
          });
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setInlineMode(null); return; }
        return;
      }

      switch (e.key) {
        case 'j': {
          e.preventDefault();
          const next = Math.min(focusIndex + 1, filtered.length - 1);
          setFocusIndex(next);
          scrollToCard(next);
          break;
        }
        case 'k': {
          e.preventDefault();
          const prev = Math.max(focusIndex - 1, 0);
          setFocusIndex(prev);
          scrollToCard(prev);
          break;
        }
        case ' ': {
          e.preventDefault();
          const item = filtered[focusIndex];
          if (!item) break;
          setSelected(prev => {
            const next = new Set(prev);
            if (next.has(item.id)) next.delete(item.id);
            else next.add(item.id);
            return next;
          });
          break;
        }
        case 'a': {
          e.preventDefault();
          if (e.shiftKey && selected.size > 0) {
            doBatchAction('approve');
          } else if (filtered[focusIndex]) {
            doAction(filtered[focusIndex], 'approve');
          }
          break;
        }
        case 'r': {
          e.preventDefault();
          if (filtered[focusIndex]) setInlineMode('reject');
          break;
        }
        case 'd': {
          e.preventDefault();
          if (e.shiftKey && selected.size > 0) {
            doBatchAction('observe');
          } else if (filtered[focusIndex]) {
            setInlineMode('defer');
          }
          break;
        }
        case 'x': {
          e.preventDefault();
          const item = filtered[focusIndex];
          if (!item) break;
          setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(item.id)) next.delete(item.id);
            else next.add(item.id);
            return next;
          });
          break;
        }
        case 'Escape': {
          e.preventDefault();
          if (selected.size > 0) setSelected(new Set());
          break;
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusIndex, filtered, inlineMode, selected, doAction, doBatchAction, scrollToCard]);

  // Clamp focus when list shrinks
  useEffect(() => {
    if (focusIndex >= filtered.length && filtered.length > 0) {
      setFocusIndex(filtered.length - 1);
    }
  }, [filtered.length, focusIndex]);

  const getConfidenceColor = (c: number) =>
    c >= 80 ? 'success' : c >= 50 ? 'warning' : 'failure';

  if (isLoading && discoveries.length === 0 && projects.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Spinner size="xl" />
      </div>
    );
  }

  if (projects.length === 0 && !isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Curation</h1>
        <div className="bg-white rounded-lg border p-12 text-center">
          <HiLightBulb className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-gray-500">No projects yet. <a href="/projects" className="text-blue-600 hover:underline">Create one</a>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" ref={containerRef}>
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Curation</h1>
          <Badge color="info">{filtered.length} pending</Badge>
          {selected.size > 0 && (
            <Badge color="purple">{selected.size} selected</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {projects.length > 1 && (
            <select
              value={selectedProjectId}
              onChange={e => setSelectedProjectId(e.target.value)}
              className="text-sm border-gray-300 rounded-md"
            >
              {projects.map(p => (
                <option key={p.project_id} value={p.project_id}>{p.project_name}</option>
              ))}
            </select>
          )}
          <Button size="xs" color="light" onClick={loadDiscoveries} disabled={isLoading}>
            <HiRefresh className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Source stats + filter */}
      {Object.keys(sourceStats).length > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => setSourceFilter('all')}
            className={`px-2 py-1 rounded text-xs font-medium ${
              sourceFilter === 'all' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            All ({discoveries.length})
          </button>
          {Object.entries(sourceStats).map(([src, count]) => (
            <button
              key={src}
              onClick={() => setSourceFilter(src)}
              className={`px-2 py-1 rounded text-xs font-medium ${
                sourceFilter === src ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {src} ({count})
            </button>
          ))}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`p-3 rounded-md flex items-center gap-2 text-sm ${
          toast.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {toast.type === 'success' ? <HiCheck className="h-4 w-4" /> : <HiExclamation className="h-4 w-4" />}
          {toast.text}
        </div>
      )}
      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm flex items-center gap-2">
          <HiExclamation className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Keyboard legend */}
      <div className="text-xs text-gray-400 flex items-center gap-3 flex-wrap">
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">j</kbd>/<kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">k</kbd> navigate</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">a</kbd> approve</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">r</kbd> reject</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">d</kbd> defer</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">x</kbd> expand</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">space</kbd> select</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">Shift+a</kbd> batch approve</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">Shift+d</kbd> batch defer</span>
      </div>

      {/* Batch action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <span className="text-sm font-medium text-purple-700">
            {selected.size} selected:
          </span>
          <span className="text-xs text-purple-600 max-w-lg truncate">
            {filtered.filter(d => selected.has(d.id)).map(d => d.name).join(', ')}
          </span>
          <div className="ml-auto flex gap-2">
            <Button size="xs" color="success" onClick={() => doBatchAction('approve')} disabled={isActioning === 'batch'}>
              Approve all
            </Button>
            <Button size="xs" color="warning" onClick={() => doBatchAction('observe')} disabled={isActioning === 'batch'}>
              Defer all
            </Button>
            <Button size="xs" color="light" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Cards */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="xl" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <HiLightBulb className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">
            {discoveries.length === 0
              ? 'Queue empty.'
              : 'No items match the current filter.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((discovery, idx) => {
            const isFocused = idx === focusIndex;
            const isSelected = selected.has(discovery.id);
            const isExpanded = expanded.has(discovery.id);
            const isProcessing = isActioning === discovery.id || isActioning === 'batch';
            const showRejectPicker = isFocused && inlineMode === 'reject';
            const showDeferPicker = isFocused && inlineMode === 'defer';

            return (
              <div
                key={discovery.id}
                ref={el => { if (el) cardRefs.current.set(idx, el); }}
                onClick={() => setFocusIndex(idx)}
                className={`rounded-lg border p-3 transition-all cursor-pointer ${
                  isFocused
                    ? 'ring-2 ring-blue-400 border-blue-300 bg-blue-50/30'
                    : isSelected
                      ? 'border-purple-300 bg-purple-50/30'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {/* Main row */}
                <div className="flex items-start gap-3">
                  {/* Selection checkbox */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      setSelected(prev => {
                        const next = new Set(prev);
                        if (next.has(discovery.id)) next.delete(discovery.id);
                        else next.add(discovery.id);
                        return next;
                      });
                    }}
                    className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    tabIndex={-1}
                  />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 truncate">
                        {discovery.name}
                      </span>
                      <Badge color="gray" className="text-xs">{discovery.source}</Badge>
                      <Badge color="info" className="text-xs">{discovery.category}</Badge>
                      <Badge color={getConfidenceColor(discovery.confidence)} className="text-xs">
                        {discovery.confidence}%
                      </Badge>
                      {discovery.evidence && (
                        <span className="text-xs text-gray-400 truncate max-w-48">{discovery.evidence}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{discovery.description}</p>

                    {/* Expanded content */}
                    {isExpanded && discovery.rule_text && (
                      <div className="mt-2 bg-gray-50 rounded p-3 border border-gray-100">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rule</span>
                        <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{discovery.rule_text}</p>
                      </div>
                    )}
                  </div>

                  {/* Expand toggle */}
                  {discovery.rule_text && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setExpanded(prev => {
                          const next = new Set(prev);
                          if (next.has(discovery.id)) next.delete(discovery.id);
                          else next.add(discovery.id);
                          return next;
                        });
                      }}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      tabIndex={-1}
                    >
                      {isExpanded ? <HiChevronUp className="h-4 w-4" /> : <HiChevronDown className="h-4 w-4" />}
                    </button>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={e => { e.stopPropagation(); doAction(discovery, 'approve'); }}
                      disabled={isProcessing}
                      className="p-1.5 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
                      title="Approve (a)"
                      tabIndex={-1}
                    >
                      <HiCheck className="h-4 w-4" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setFocusIndex(idx); setInlineMode('reject'); }}
                      disabled={isProcessing}
                      className="p-1.5 rounded hover:bg-red-100 text-red-600 disabled:opacity-50"
                      title="Reject (r)"
                      tabIndex={-1}
                    >
                      <HiX className="h-4 w-4" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setFocusIndex(idx); setInlineMode('defer'); }}
                      disabled={isProcessing}
                      className="p-1.5 rounded hover:bg-amber-100 text-amber-600 disabled:opacity-50"
                      title="Defer (d)"
                      tabIndex={-1}
                    >
                      <HiClock className="h-4 w-4" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); doAction(discovery, 'observe'); }}
                      disabled={isProcessing}
                      className="p-1.5 rounded hover:bg-blue-100 text-blue-600 disabled:opacity-50"
                      title="Keep Observing"
                      tabIndex={-1}
                    >
                      <HiEye className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Inline reject picker */}
                {showRejectPicker && (
                  <div className="mt-2 ml-8 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-xs font-medium text-red-700 mb-1 block">
                      Pick reason (1-4, Esc to cancel):
                    </span>
                    <div className="flex gap-2">
                      {REASON_CODES.map(rc => (
                        <button
                          key={rc.value}
                          onClick={() => doAction(discovery, 'reject', { reason_code: rc.value })}
                          className="px-2 py-1 text-xs rounded border border-red-200 hover:bg-red-100 text-red-700"
                        >
                          <kbd className="font-mono mr-1">{rc.key}</kbd>{rc.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inline defer picker */}
                {showDeferPicker && (
                  <div className="mt-2 ml-8 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <span className="text-xs font-medium text-amber-700 mb-1 block">
                      Defer until (1-4, Esc to cancel):
                    </span>
                    <div className="flex gap-2">
                      {DEFER_PRESETS.map(dp => {
                        const date = new Date();
                        date.setDate(date.getDate() + dp.days);
                        return (
                          <button
                            key={dp.key}
                            onClick={() => doAction(discovery, 'observe', {
                              reason: `deferred until ${date.toISOString().slice(0, 10)}`,
                            })}
                            className="px-2 py-1 text-xs rounded border border-amber-200 hover:bg-amber-100 text-amber-700"
                          >
                            <kbd className="font-mono mr-1">{dp.key}</kbd>{dp.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Processing overlay */}
                {isProcessing && isActioning === discovery.id && (
                  <div className="mt-2 ml-8 flex items-center gap-2 text-xs text-gray-500">
                    <Spinner size="xs" /> Processing...
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DiscoveryReview;
